"use client";

import { useEffect } from "react";
import { useAuthContext } from "@/context/authContext";
import { sendGtagEvent } from "@/lib/client/gtag";
import { sendRedditPixelEvent } from "@/lib/client/redditPixel";

const PRO_STATE_KEY_PREFIX = "kurb_pro_state_";

/**
 * Fires a one-time conversion event on an observed false -> true transition
 * of isPaidProUser (the raw paid-plan check, not the effective value that
 * also covers admin/free-pro grants — see authContext.tsx).
 *
 * Deliberately does NOT fire on the first check ever recorded for a user
 * even if already true: without a prior recorded false, there's no way to
 * tell an existing long-time subscriber's ordinary page load apart from a
 * brand-new purchase, so only a recorded false immediately followed by true
 * counts as a real conversion.
 *
 * Uses localStorage, not sessionStorage: Clerk's PricingTable checkout may
 * involve a full page redirect away and back rather than staying on a
 * same-page modal — sessionStorage would be empty on that fresh reload,
 * with no recorded false baseline to compare against, silently missing the
 * conversion entirely. localStorage survives that redirect for anyone with
 * at least one prior KURB visit before checking out (the realistic case) —
 * it only still misses a visitor whose very first-ever page load goes
 * straight to a completed purchase, an acceptable, rare edge case.
 */
const TrackProSubscription = () => {
  const { isPaidProUser, userId, isLoaded } = useAuthContext();

  useEffect(() => {
    if (!isLoaded || !userId) return;

    const stateKey = `${PRO_STATE_KEY_PREFIX}${userId}`;
    const previousState = localStorage.getItem(stateKey);

    if (previousState === "false" && isPaidProUser) {
      // Not named "purchase" — that's a GA4-reserved event name expecting
      // value/currency/transaction_id/items to populate Monetization
      // reports; firing it without those just leaves an incomplete event
      // in DebugView rather than actually being usable.
      sendGtagEvent("pro_subscription_started", "conversion", "pro_subscription");
      // TODO: pass `value`/`currency` once there's a reliable client-side
      // source for the actual plan price (Clerk's PricingTable handles
      // checkout internally and doesn't expose one here) — the CAPI/
      // backend integration, driven off Clerk's billing webhook, is the
      // more accurate place to get the real charged amount anyway.
      sendRedditPixelEvent("Purchase", { currency: "USD" });
    }

    localStorage.setItem(stateKey, String(isPaidProUser));
  }, [isPaidProUser, userId, isLoaded]);

  return null;
};

export default TrackProSubscription;
